/**
 * Regex pattern to match existing markdown links with circuit:// protocol
 * that contain characters needing URL encoding.
 *
 * Key features:
 * - Lookahead (?=[^\n[\]]*[ ()&]) ensures we only match URLs with unencoded
 *   spaces, parentheses, or ampersands - skipping already-encoded URLs
 * - Extension pattern (\.\w{2,5}) anchors the end, letting us distinguish
 *   ) after ".csv" (closes link) from ) after "(2024" (part of path)
 * - [^\n[\]] excludes newlines and brackets to prevent matching across
 *   lines or multiple markdown links
 */
const MARKDOWN_LINK_PATTERN =
  /\[(?<displayText>[^\]]{1,500})\]\(circuit:\/\/(?=[^\n[\]]*[ ()&])(?<urlPath>[^\n[\]]{1,1000}?\.\w{2,5}(?::\d+(?::\d+)?)?)\)/gu;

/**
 * Encodes a file path for use in a URL while preserving slashes.
 * Encodes each path segment separately and rejoins with slashes.
 * Also encodes parentheses which are not encoded by encodeURIComponent
 * but need to be escaped in markdown link syntax.
 */
function encodeFilePath(path: string): string {
  const colonParts = path.split(':');
  const pathPart = colonParts[0];
  const lineNumbers = colonParts.slice(1);

  const encodedPath = pathPart
    .split('/')
    .map((segment) => {
      const encoded = encodeURIComponent(segment);
      return encoded.replace(/\(/gu, '%28').replace(/\)/gu, '%29');
    })
    .join('/');

  return lineNumbers.length > 0
    ? `${encodedPath}:${lineNumbers.join(':')}`
    : encodedPath;
}

/**
 * Fixes encoding in existing markdown links with circuit:// protocol.
 * Re-encodes the URL part while preserving the display text.
 */
function fixExistingMarkdownLinks(content: string): string {
  return content.replace(
    MARKDOWN_LINK_PATTERN,
    (_: string, displayText: string, urlPath: string) => {
      // Decode first in case it's partially encoded, then re-encode properly
      let decodedPath: string;
      try {
        decodedPath = decodeURIComponent(urlPath);
      } catch {
        // If decoding fails, use as-is
        decodedPath = urlPath;
      }

      const encodedPath = encodeFilePath(decodedPath);
      return `[${displayText}](circuit://${encodedPath})`;
    },
  );
}

/**
 * Preprocesses content to auto-link file paths to circuit:// protocol.
 */
export function preprocessFilePaths(content: string): string {
  // fix any existing markdown links with unencoded URLs
  return fixExistingMarkdownLinks(content);
}
